import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import useAuth from '@contexts/useAuth';
import { billingGetUsage } from '@api/billing';
import type { CreditTransaction, TransactionHistoryPage as HistoryPage } from '@api/billing';
import Button, {
  BUTTON_VARIANT_TEXT,
} from '@components/1-atoms/Button';
import ConversationGroup from './ConversationGroup';
import ProcessRunGroup from './ProcessRunGroup';
import {
  HistoryContainer,
  EmptyState,
  LoadingState,
  PaginationRow,
  StandaloneRow,
  StandaloneLeft,
  StandaloneType,
  StandaloneDate,
  StandaloneAmount,
} from './TransactionHistory.styled';

const TX_TYPE_LABELS: Record<string, { id: string; defaultMessage: string }> = {
  PURCHASE: { id: 'billing.txType.purchase', defaultMessage: 'Purchase' },
  SUBSCRIPTION: { id: 'billing.txType.subscription', defaultMessage: 'Subscription Credit' },
  BONUS: { id: 'billing.txType.bonus', defaultMessage: 'Bonus' },
  REFUND: { id: 'billing.txType.refund', defaultMessage: 'Refund' },
  ADMIN_GRANT: { id: 'billing.txType.adminGrant', defaultMessage: 'Admin Grant' },
  ADMIN_DEBIT: { id: 'billing.txType.adminDebit', defaultMessage: 'Admin Debit' },
  MIGRATED_GRANT: { id: 'billing.txType.migratedGrant', defaultMessage: 'Migration Credit' },
  EXPIRY: { id: 'billing.txType.expiry', defaultMessage: 'Expired' },
};

const SOURCE_APP_LABELS: Record<string, { id: string; defaultMessage: string }> = {
  CAM: { id: 'billing.sourceApp.cam', defaultMessage: 'Aibuild CAM' },
  OS: { id: 'billing.sourceApp.os', defaultMessage: 'Aibuild OS' },
  'OS-PROCESS': { id: 'billing.sourceApp.osProcess', defaultMessage: 'Aibuild OS — Processes' },
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const PAGE_SIZE = 20;

const TransactionHistory: React.FC = () => {
  const intl = useIntl();
  const { user } = useAuth();
  const [page, setPage] = useState(0);

  const query = useQuery<HistoryPage>({
    queryKey: ['billingUsage', user?.id, page],
    queryFn: () => billingGetUsage(user!.token!, page, PAGE_SIZE),
    enabled: !!user?.token,
    placeholderData: keepPreviousData,
  });

  const typeLabel = (type: string): string => {
    const msg = TX_TYPE_LABELS[type];
    return msg ? intl.formatMessage(msg) : type;
  };

  const standaloneLabel = (tx: CreditTransaction): string => {
    if (tx.type === 'CONSUMPTION') {
      const msg = SOURCE_APP_LABELS[tx.sourceApp];
      if (msg) {
        return intl.formatMessage(msg);
      }
    }
    return typeLabel(tx.type);
  };

  if (query.isLoading) {
    return (
      <HistoryContainer>
        <LoadingState>
          {intl.formatMessage({ id: 'billing.usage.loading', defaultMessage: 'Loading...' })}
        </LoadingState>
      </HistoryContainer>
    );
  }

  if (query.isLoadingError) {
    return (
      <HistoryContainer>
        <EmptyState>
          {intl.formatMessage({ id: 'billing.usage.loadError', defaultMessage: 'Failed to load transactions.' })}
        </EmptyState>
      </HistoryContainer>
    );
  }

  const data = query.data;
  if (!data || data.items.length === 0) {
    return (
      <HistoryContainer>
        <EmptyState>
          {intl.formatMessage({ id: 'billing.usage.empty', defaultMessage: 'No transactions yet.' })}
        </EmptyState>
      </HistoryContainer>
    );
  }

  return (
    <HistoryContainer data-testid="transaction-history">
      {data.items.map((item) => {
        if (item.itemType === 'CONVERSATION' && item.conversation) {
          return (
            <ConversationGroup
              key={item.conversation.conversationId}
              summary={item.conversation}
            />
          );
        }
        if (item.itemType === 'PROCESS_RUN' && item.processRun) {
          return (
            <ProcessRunGroup
              key={item.processRun.runId}
              summary={item.processRun}
            />
          );
        }
        if (item.itemType === 'STANDALONE' && item.transaction) {
          const tx = item.transaction;
          return (
            <StandaloneRow key={tx.id} data-testid="standalone-transaction">
              <StandaloneLeft>
                <StandaloneType>{standaloneLabel(tx)}</StandaloneType>
                <StandaloneDate>{formatDateTime(tx.createdAt)}</StandaloneDate>
              </StandaloneLeft>
              <StandaloneAmount $positive={tx.amount > 0}>
                {tx.amount > 0 ? '+' : ''}
                {tx.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                {' '}
                {intl.formatMessage({ id: 'billing.usage.credits', defaultMessage: 'credits' })}
              </StandaloneAmount>
            </StandaloneRow>
          );
        }
        const unexpected: never = item;
        console.warn(
          '[TransactionHistory] Unknown itemType:',
          (unexpected as unknown as { itemType: string }).itemType,
          unexpected,
        );
        return null;
      })}

      {data.totalPages > 1 && (
        <PaginationRow>
          <Button
            variant={BUTTON_VARIANT_TEXT}
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            stretch={false}
          >
            {intl.formatMessage({ id: 'billing.transactions.previous', defaultMessage: 'Previous' })}
          </Button>
          <span>
            {intl.formatMessage(
              { id: 'billing.transactions.pageInfo', defaultMessage: 'Page {current} of {total}' },
              { current: page + 1, total: data.totalPages },
            )}
          </span>
          <Button
            variant={BUTTON_VARIANT_TEXT}
            disabled={page >= data.totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            stretch={false}
          >
            {intl.formatMessage({ id: 'billing.transactions.next', defaultMessage: 'Next' })}
          </Button>
        </PaginationRow>
      )}
    </HistoryContainer>
  );
};

export default TransactionHistory;
